// frontend/src/modules/expenses/components/ReceiptUpload.tsx
import * as React from 'react';
import { motion } from 'framer-motion';
import { UploadCloud, FileText, Image as ImageIcon, X, Loader2, Download } from 'lucide-react';
import { cn } from '../../../lib/utils';
import type { Attachment } from '../types';

type PendingFile = { id: string; file: File; status: 'queued' | 'uploading' | 'error'; error?: string };

interface ReceiptUploadProps {
  /** Existing attachments (already saved on the expense). */
  attachments?: Attachment[];
  /** Pending local files that haven't been uploaded yet. */
  pending?: PendingFile[];
  /** Called when the user drops or picks files. */
  onAdd?: (files: File[]) => void;
  /** Called when a pending file is removed before upload. */
  onRemovePending?: (id: string) => void;
  /** Called when an already-uploaded attachment is deleted. */
  onRemoveAttachment?: (attachmentId: string) => void;
  disabled?: boolean;
  /** Hides the drop zone — useful for read-only views. */
  readOnly?: boolean;
  className?: string;
}

const ACCEPT = 'image/*,.pdf,application/pdf';

export function ReceiptUpload({
  attachments = [],
  pending = [],
  onAdd,
  onRemovePending,
  onRemoveAttachment,
  disabled,
  readOnly,
  className,
}: ReceiptUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = React.useState(false);

  function pickFiles(filelist: FileList | null) {
    if (!filelist || filelist.length === 0) return;
    const files = Array.from(filelist);
    onAdd?.(files);
  }

  const items: Array<
    | { kind: 'attached'; att: Attachment }
    | { kind: 'pending'; p: PendingFile }
  > = [
    ...attachments.map((att) => ({ kind: 'attached' as const, att })),
    ...pending.map((p) => ({ kind: 'pending' as const, p })),
  ];

  return (
    <div className={cn('space-y-3', className)}>
      {!readOnly && (
        <label
          onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (disabled) return;
            pickFiles(e.dataTransfer.files);
          }}
          className={cn(
            'block rounded-2xl border-2 border-dashed transition-all cursor-pointer p-6 text-center',
            'bg-white/40 dark:bg-white/[0.03] backdrop-blur-md',
            dragOver
              ? 'border-coral/60 bg-coral/5'
              : 'border-ink/15 dark:border-white/15 hover:border-ink/30 dark:hover:border-white/25',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="sr-only"
            disabled={disabled}
            onChange={(e) => { pickFiles(e.target.files); e.target.value = ''; }}
          />
          <div className="mx-auto mb-3 h-10 w-10 rounded-2xl bg-coral/12 flex items-center justify-center">
            <UploadCloud className="h-5 w-5 text-coral-600" strokeWidth={1.6} />
          </div>
          <p className="text-[13.5px] font-semibold text-ink dark:text-white">
            Drop receipts here, or <span className="text-coral-600 underline">browse</span>
          </p>
          <p className="text-[12px] text-ink/55 dark:text-white/55 mt-1">
            PNG, JPG, or PDF. Multiple files allowed.
          </p>
        </label>
      )}

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it) => {
            if (it.kind === 'attached') {
              const a = it.att;
              return (
                <motion.li
                  key={`a-${a.id}`}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-3 px-3.5 py-2.5 rounded-2xl bg-white/60 dark:bg-white/[0.05] ring-1 ring-ink/8 dark:ring-white/10"
                >
                  <FileIcon mime={a.mime_type} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-ink dark:text-white truncate">{a.file_name}</p>
                    <p className="text-[11.5px] text-ink/55 dark:text-white/50">{prettySize(a.file_size)}</p>
                  </div>
                  <a
                    href={a.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink/55 hover:text-ink dark:text-white/55 dark:hover:text-white hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                    aria-label={`Download ${a.file_name}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </a>
                  {!readOnly && onRemoveAttachment && (
                    <button
                      type="button"
                      onClick={() => onRemoveAttachment(a.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink/55 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                      aria-label={`Remove ${a.file_name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </motion.li>
              );
            }
            const p = it.p;
            return (
              <motion.li
                key={`p-${p.id}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  'flex items-center gap-3 px-3.5 py-2.5 rounded-2xl ring-1',
                  p.status === 'error'
                    ? 'bg-rose-50 dark:bg-rose-500/10 ring-rose-500/30'
                    : 'bg-white/55 dark:bg-white/[0.04] ring-ink/8 dark:ring-white/10',
                )}
              >
                <FileIcon mime={p.file.type} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-ink dark:text-white truncate">{p.file.name}</p>
                  <p className="text-[11.5px] text-ink/55 dark:text-white/50">
                    {p.status === 'uploading' ? 'Uploading…' : p.status === 'error' ? (p.error ?? 'Upload failed') : 'Ready to upload'}
                    {' · '}{prettySize(p.file.size)}
                  </p>
                </div>
                {p.status === 'uploading' ? (
                  <Loader2 className="h-3.5 w-3.5 text-ink/55 animate-spin" />
                ) : (
                  onRemovePending && (
                    <button
                      type="button"
                      onClick={() => onRemovePending(p.id)}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink/55 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors"
                      aria-label={`Remove ${p.file.name}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )
                )}
              </motion.li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FileIcon({ mime }: { mime: string }) {
  const isImg = mime.startsWith('image/');
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-coral/12 text-coral-600 shrink-0">
      {isImg ? <ImageIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
    </span>
  );
}

function prettySize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

export type { PendingFile };
